<template>
  <section class="bg-white py-space-lg py-lg-space-xxl">
    <div class="container">
      <div class="row" :class="rowClass">
        <!--image -->
        <div class="col-12 col-lg-5" :class="imageContainerClass">
          <div class="position-relative">
            <div
              class="description-img mb-space-lg mb-lg-0"
              :class="{ 'ssa-lazy': bgImageLazy }"
              :data-background-image="dataBgImage"
              :style="bgImage"></div>
            <template v-if="displayStep">
              <div class="step-circle position-absolute" :class="stepBgClass">
                <div
                  class="step-number text-center mt-lg-space-xs fw-bold"
                  :class="stepClass">
                  <slot name="circle">{{ stepNumber }}</slot>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- text -->
        <div class="col-12 col-lg-7">
          <div
            class="bg-white p-lg-space-lg mt-lg-space-xxl mt-xxl-space-xxxl description-div">
            <h2 class="h2-medium mb-space-sm" :class="titleClass">
              {{ title }}
            </h2>
            <div class="text-start">
              <slot></slot>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import lozad from "lozad";
import { computed, onMounted } from "vue";

interface Props {
  title: string;
  titleVariant?: string;
  titleSize?: "xl" | "large" | "small" | "medium";
  imageSource: string;
  imagePosition?: "left" | "right";
  stepNumber?: string;
  stepVariant?: string;
  stepBgVariant?: string;
  bgImageLazy?: boolean;
  displayStep?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  titleVariant: "dark-3",
  imagePosition: "left",
  displayStep: true,
  stepBgVariant: "secondary",
  stepVariant: "dark-3",
});

const bgImage = computed(() => {
  if (!props.bgImageLazy) {
    return { "background-image": `url('${props.imageSource}')` };
  } else {
    return null;
  }
});

const rowClass = computed(() => {
  return props.imagePosition == "left" ? `image-side-left` : `image-side-right`;
});

const imageContainerClass = computed(() => {
  return props.imagePosition == "left" ? null : `order-lg-2`;
});

const stepClass = computed(() => {
  return `text-${props.stepVariant}`;
});

const stepBgClass = computed(() => {
  return `bg-${props.stepBgVariant}`;
});

const titleClass = computed(() => {
  return `h2-${props.titleSize} text-${props.titleVariant}`;
});

const dataBgImage = computed(() => {
  return props.bgImageLazy ? props.imageSource : null;
});

onMounted(() => {
  if (props.bgImageLazy) {
    const observer = lozad(".ssa-lazy");
    observer.observe();
  }
});
</script>

<style lang="scss" scoped>
.description-img {
  border-radius: 50%;
  background-position: 50%;
  background-repeat: no-repeat;
  background-size: cover;
  height: 281px;
  width: 281px;
  margin: auto;
}
.step-circle {
  height: 64px;
  width: 64px;
  top: 95px;
  border-radius: 50%;
}
.step-number {
  font-size: 36px;
  margin-top: 6px;
}
/* Medium devices (tablets, 768px and up) */
@media (min-width: 768px) {
  .description-img {
    height: 350px;
    width: 350px;
  }
  .step-circle {
    height: 64px;
    width: 64px;
    top: 140px;
    left: 130px;
  }
}
/* Large devices (desktops, 992px and up) */
@media (min-width: 992px) {
  .description-img {
    width: 635px;
    height: 635px;
    .image-side-left & {
      margin-right: 15px;
    }
    .image-side-right & {
      margin-left: -230px;
    }
  }
  .description-div {
    min-height: 350px;
    max-width: 635px;
    z-index: 1;
    position: relative;
  }
  .step-circle {
    top: 0;
    height: 128px;
    width: 128px;
    .image-side-left & {
      left: 64px;
    }
    .image-side-right & {
      right: 60px;
      left: auto;
    }
  }
  .step-number {
    font-size: 60px;
  }
}

/* Large devices (desktops, 1400px and up) */
@media (min-width: 1400px) {
  .description-img {
    .image-side-right & {
      margin-left: -180px;
    }
  }
  .description-div {
    .image-side-right & {
      max-width: 710px;
    }
  }
  .step-circle {
    .image-side-right & {
      right: 85px;
    }
  }
}
</style>
